import { Button, Modal } from '@dev-spendesk/grapes';
import { useFormik } from 'formik';
import { useEffect } from 'react';

import { useTranslation } from 'common/hooks/useTranslation';
import { type SyncMode } from 'src/core/modules/accounting-integration/apis/useSetSyncSettings';
import {
  type AnalyticalFieldValueMappingV2,
  type AnalyticalFieldMappingV2,
} from 'src/core/modules/bookkeep';

import { type CustomField } from '../../models/customField';
import { CustomFieldValuesStep } from '../CustomFieldCreationModal/CustomFieldCreationModal';
import { validate } from '../CustomFieldCreationModal/validate';

type Props = {
  isOpen: boolean;
  customField: CustomField;
  name: string;
  onClose(): void;
  onSubmit(values: string): void;
  isMapSyncEnabled: boolean;
  fieldMapping: AnalyticalFieldMappingV2 | undefined;
  syncMode?: SyncMode;
  valueMapping?: AnalyticalFieldValueMappingV2;
  setValueMapping: React.Dispatch<
    React.SetStateAction<AnalyticalFieldValueMappingV2 | undefined>
  >;
};

export const CustomFieldEditValuesModal = ({
  onClose,
  isOpen,
  customField,
  name,
  onSubmit,
  isMapSyncEnabled,
  fieldMapping,
  syncMode,
  valueMapping,
  setValueMapping,
}: Props) => {
  const { t } = useTranslation('global');

  const formikProps = useFormik<Parameters<typeof validate>[0]>({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: {
      customFieldValues: undefined,
    },
    validate: (values) => {
      return validate(values, customField.totalValues);
    },
    onSubmit: (values) => {
      if (values.customFieldValues) {
        onSubmit(values.customFieldValues);
      }
    },
  });

  useEffect(() => {
    formikProps.resetForm();
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      title={t('customFields.edition.modalTitle', { name })}
      iconName="pen"
      iconVariant="info"
      actions={[
        <Button
          key="cancel-button"
          text={t('misc.cancel')}
          variant="secondaryNeutral"
          onClick={onClose}
        />,
        <Button
          key="submit-button"
          text={t('customFields.edition.modalSubmit')}
          isDisabled={formikProps.isSubmitting}
          onClick={() => formikProps.handleSubmit()}
        />,
      ]}
    >
      <CustomFieldValuesStep
        existingValuesCount={customField.totalValues}
        formikProps={formikProps}
        isMapSyncEnabled={isMapSyncEnabled}
        fieldMapping={fieldMapping}
        syncMode={syncMode}
        valueMapping={valueMapping}
        setValueMapping={setValueMapping}
      />
    </Modal>
  );
};
